import { reactive, watch, computed, Ref } from "vue";
import { compareAsc } from "date-fns";

import { MedicalCondition, SubConditions } from "@/entities/conditions";

export function useMedicalForm<T extends MedicalCondition>(
    condition: T,
    emit: (on: "update:condition", value: T) => void,
) {
    const state = reactive({
        condition: condition as unknown as Ref<T>,
    });

    watch(state.condition, () => {
        emit("update:condition", state.condition);
    });

    const onConditionUpdate = (condition: T) => {
        state.condition = condition;
        emit("update:condition", condition);
    };

    return { onConditionUpdate, state };
}

export function useMedicalFormWithSubConditions<T extends MedicalCondition, V extends SubConditions>(
    initialState: {
        condition: T,
        newCondition: V | null
    },
    emit: (on: "update:condition", value: T) => void,
) {
    const state = reactive({
        ...initialState,
        newCondition: initialState.newCondition as unknown as Ref<V | null>,
        condition: initialState.condition as unknown as Ref<T>,
        isEditSubCondition: false,
        hasDiagnosisDateChanged: false,
        prevDiagnosisDate: null as unknown as Ref<string | null>,
    });

    const minSubConditionDate = computed(() => {
        if (!state.condition.subConditions?.length) {
            return null;
        }
        const sortedDates = state.condition.subConditions.map(
            cond => new Date(cond.diagnosisdate)).sort(compareAsc);

        return sortedDates[0];
    });

    const handleUpdate = (items: V[]) => {
        state.condition.subConditions = [...items];
        emit("update:condition", state.condition);
    };

    const handleUpdateItem = (item: V | null) => {
        state.newCondition = item;

        if (item === null && state.condition.subConditions?.length === 0) {
            emit("update:condition", state.condition);
        }
    };

    const handleSave = (items: V[]) => {
        state.newCondition = null;
        state.condition.subConditions = [...items];
        emit("update:condition", state.condition);
    };

    const onConditionUpdate = (condition: T) => {
        state.condition = condition;
        emit("update:condition", condition);
    };

    watch(state.condition, () => {
        emit("update:condition", state.condition);
    });

    return { onConditionUpdate, minSubConditionDate, state, handleUpdate, handleUpdateItem, handleSave };
}
